import { getMeOptions, getOnboardingSessionOptions } from '@labflow/api-client';
import type { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';

import { problemStatus } from '../../lib/problem-details';
import { serverFeaturesEnabled } from '../../lib/server-features';
import { internalReturnPath, requireSession } from '../auth/require-session';

export { internalReturnPath };

/** 필수 동의를 받는 화면 — 온보딩보다 앞이다 ([D113] · `AC-PF-01-16`) */
export const CONSENT_ROUTE = '/account/consent';

export const ONBOARDING_ROUTES = [
  '/onboarding/upload',
  '/onboarding/analyzing',
  '/onboarding/review',
  '/onboarding/profile',
] as const;
export type OnboardingRoute = (typeof ONBOARDING_ROUTES)[number];
export const FALLBACK_ROUTE: OnboardingRoute = '/onboarding/upload';

export function isOnboardingRoute(route: string | null): route is OnboardingRoute {
  return ONBOARDING_ROUTES.some((candidate) => candidate === route);
}

export async function requireOnboarding({
  queryClient,
  location,
}: {
  queryClient: QueryClient;
  location: { href: string };
}): Promise<void> {
  if (!serverFeaturesEnabled) return;

  const me = await requireSession({ queryClient, location });

  // 동의는 **온보딩 완료 여부와 무관하다** — 문서 버전이 올라가면 완료한 사용자도 다시 받는다
  // (`AC-PF-01-16`). 완료 사용자를 먼저 통과시키면 그 경로가 영원히 닫힌다.
  if (me.consent_required) throw redirect({ to: CONSENT_ROUTE, replace: true });

  if (me.onboarding_completed_at) return;

  const session = await queryClient.ensureQueryData(getOnboardingSessionOptions());
  const nextRoute = session.next_route;
  throw redirect({
    to: isOnboardingRoute(nextRoute) ? nextRoute : FALLBACK_ROUTE,
    replace: true,
  });
}

export async function requireOnboardingForPublicRoute({
  queryClient,
}: {
  queryClient: QueryClient;
}): Promise<void> {
  if (!serverFeaturesEnabled) return;

  let me;
  try {
    me = await queryClient.ensureQueryData({ ...getMeOptions(), retry: false });
  } catch (error) {
    if (problemStatus(error) === 401) return;
    throw error;
  }

  if (me.consent_required) throw redirect({ to: CONSENT_ROUTE, replace: true });

  if (me.onboarding_completed_at) return;

  const session = await queryClient.ensureQueryData(getOnboardingSessionOptions());
  const nextRoute = session.next_route;
  throw redirect({
    to: isOnboardingRoute(nextRoute) ? nextRoute : FALLBACK_ROUTE,
    replace: true,
  });
}
